import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import GetComplexCollectionOnFilterQuery from 'bundles/page-config-common/queries/GetComplexCollectionOnFilterQuery.graphql';
import type {
  ComplexCollectionOnFilterElementFragment as Entity,
  GetComplexCollectionOnFilterQuery as GetComplexCollectionOnFilterQueryType,
  GetComplexCollectionOnFilterQueryVariables,
} from 'bundles/page-config-common/queries/__generated__/GetComplexCollectionOnFilterQuery';
import type {
  PageConfig_CollectionComponentCollectionEntityFragment as Collection,
  PageConfig_FiltersFragment as Filter,
  PageConfig_CollectionComponentCollectionEntityRecommendationFragment as RecommendationCollection,
} from 'bundles/page-config-common/queries/page-config/__generated__/PageConfig_CollectionComponentMetadata';

type Props = {
  additionalInput?: Record<string, unknown>;
  collection: Collection | RecommendationCollection;
  filters?: Filter[];
};

// Type guard for ProductCard_ProductCard
const isProductCardProductCard = (element: unknown): element is Entity => {
  return (
    typeof element === 'object' &&
    element !== null &&
    '__typename' in element &&
    (element as { __typename?: string }).__typename === 'ProductCard_ProductCard'
  );
};

// Safe JSON parsing with fallback
export const parseFilterValue = (value: string | undefined): Record<string, string> => {
  if (!value || value.trim() === '') {
    return {};
  }

  try {
    return JSON.parse(value) || {};
  } catch {
    return {};
  }
};

const useComplexCollectionOnFilterData = ({ additionalInput, collection, filters = [] }: Props) => {
  // Get only ProductCard_ProductCard entities from initial data
  const allElements = collection?.entities?.elements || [];
  const initialEntities: Entity[] = allElements.reduce<Entity[]>((acc, element) => {
    if (isProductCardProductCard(element)) acc.push(element);
    return acc;
  }, []);

  const [activeFilterIndex, setActiveFilterIndex] = useState<number>(0);

  const filterValue = filters[activeFilterIndex]?.value;

  // Memoize the parsed filter to avoid recreating on every render
  const filter = useMemo(() => parseFilterValue(filterValue), [filterValue]);

  // Memoize the variables object to prevent unnecessary re-renders and requests
  const queryVariables = useMemo(
    () => ({
      id: collection.id,
      additionalInput: {
        ...additionalInput,
        ...filter,
      },
    }),
    [collection.id, additionalInput, filter]
  );

  // Query collection data only when a non-default filter is selected
  const { loading, data } = useQuery<GetComplexCollectionOnFilterQueryType, GetComplexCollectionOnFilterQueryVariables>(
    GetComplexCollectionOnFilterQuery,
    {
      variables: queryVariables,
      skip: activeFilterIndex === 0,
      fetchPolicy: 'cache-first',
      context: { clientName: 'gatewayGql' },
    }
  );

  // Derive entities from query data and activeFilterIndex
  const entities = useMemo(() => {
    if (activeFilterIndex === 0) {
      return initialEntities;
    }

    const queryElements = data?.Recommendations?.queryCollection?.entities?.elements;
    if (queryElements) {
      return queryElements.reduce<Entity[]>((acc, element) => {
        if (isProductCardProductCard(element)) acc.push(element);
        return acc;
      }, []);
    }

    return initialEntities;
  }, [activeFilterIndex, data?.Recommendations?.queryCollection?.entities?.elements, initialEntities]);

  const handleFilterChange = (index: number) => {
    if (index < 0 || index >= filters.length) return;
    setActiveFilterIndex(index);
    if (index === 0) {
      // Reset to initial entities if first filter is selected
      setActiveFilterIndex(0);
    }
  };

  return {
    entities,
    loading,
    activeFilterIndex,
    handleFilterChange,
  };
};

export default useComplexCollectionOnFilterData;
